import { useState } from 'react';
import { Users, X, Search, UserPlus, Check, UserMinus, BarChart3, Globe } from 'lucide-react';
import type { FriendWithProfile, UserProfile } from '@/types';

interface FriendsPanelProps {
  friends: FriendWithProfile[];
  pendingRequests: FriendWithProfile[];
  sentRequests: FriendWithProfile[];
  profile: UserProfile | null;
  onClose: () => void;
  onSearchUsers: (email: string) => Promise<UserProfile[]>;
  onSendRequest: (userId: string) => Promise<void>;
  onAcceptRequest: (userId: string) => Promise<void>;
  onRemoveFriend: (userId: string) => Promise<void>;
  onViewComparison: (friend: FriendWithProfile) => void;
  onViewFriendGlobe: (friend: FriendWithProfile) => void;
}

export function FriendsPanel({
  friends,
  pendingRequests,
  sentRequests,
  onClose,
  onSearchUsers,
  onSendRequest,
  onAcceptRequest,
  onRemoveFriend,
  onViewComparison,
  onViewFriendGlobe,
}: FriendsPanelProps) {
  const [activeTab, setActiveTab] = useState<'friends' | 'pending' | 'search'>('friends');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    setSearching(true);
    try {
      const results = await onSearchUsers(searchEmail);
      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="glass rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-primary-500" />
            <h2 className="text-2xl font-bold">Friends</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 p-4 border-b border-white/10">
          <button
            onClick={() => setActiveTab('friends')}
            className={`px-4 py-2 rounded-lg transition ${
              activeTab === 'friends'
                ? 'bg-primary-500/20 text-primary-500 border border-primary-500/50'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            Friends ({friends.length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 rounded-lg transition relative ${
              activeTab === 'pending'
                ? 'bg-primary-500/20 text-primary-500 border border-primary-500/50'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            Requests ({pendingRequests.length})
            {pendingRequests.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                {pendingRequests.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('search')}
            className={`px-4 py-2 rounded-lg transition ${
              activeTab === 'search'
                ? 'bg-primary-500/20 text-primary-500 border border-primary-500/50'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            Add Friend
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto flight-scroll p-6">
          {activeTab === 'friends' && (
            <div className="space-y-3">
              {friends.length === 0 ? (
                <div className="text-center py-12 text-white/50">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg mb-2">No friends yet</p>
                  <p className="text-sm">Search for friends to get started!</p>
                </div>
              ) : (
                friends.map((friend) => (
                  <div key={friend.friendId} className="glass-card p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-500 font-semibold">
                        {friend.profile?.profilePicture ? (
                          <img src={friend.profile.profilePicture} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          friend.profile?.displayName?.charAt(0).toUpperCase() || '?'
                        )}
                      </div>
                      <div>
                        <p className="font-semibold">{friend.profile?.displayName || 'Unknown User'}</p>
                        {friend.profile?.email && (
                          <p className="text-sm text-white/60">{friend.profile.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onViewFriendGlobe(friend)}
                        className="btn-secondary flex items-center gap-2"
                        title="View their globe"
                      >
                        <Globe className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onViewComparison(friend)}
                        className="btn-secondary flex items-center gap-2"
                        title="Compare stats"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onRemoveFriend(friend.friendId)}
                        className="btn-ghost text-red-400 hover:bg-red-500/10"
                        title="Remove friend"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'pending' && (
            <div className="space-y-3">
              {pendingRequests.length === 0 ? (
                <div className="text-center py-12 text-white/50">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No pending requests</p>
                </div>
              ) : (
                pendingRequests.map((request) => (
                  <div key={request.friendId} className="glass-card p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-500 font-semibold">
                        {request.profile?.profilePicture ? (
                          <img src={request.profile.profilePicture} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          request.profile?.displayName?.charAt(0).toUpperCase() || '?'
                        )}
                      </div>
                      <div>
                        <p className="font-semibold">{request.profile?.displayName || 'Unknown User'}</p>
                        {request.profile?.email && (
                          <p className="text-sm text-white/60">{request.profile.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onAcceptRequest(request.friendId)}
                        className="btn-primary flex items-center gap-2"
                      >
                        <Check className="w-4 h-4" />
                        Accept
                      </button>
                      <button
                        onClick={() => onRemoveFriend(request.friendId)}
                        className="btn-ghost text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'search' && (
            <div>
              <div className="flex gap-2 mb-6">
                <div className="flex-1">
                  <input
                    type="text"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search by username..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 focus:outline-none focus:border-primary-500/50"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={searching}
                  className="btn-primary flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </div>

              <div className="space-y-3">
                {searchResults.map((user) => {
                  const isAlreadyFriend = friends.some(f => f.friendId === user.userId);
                  const hasPendingRequest = sentRequests.some(r => r.friendId === user.userId);
                  
                  return (
                    <div key={user.userId} className="glass-card p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-500 font-semibold">
                          {user.profilePicture ? (
                            <img src={user.profilePicture} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            user.displayName.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">{user.displayName}</p>
                        </div>
                      </div>
                      {isAlreadyFriend ? (
                        <span className="text-sm text-white/50">Already Friends</span>
                      ) : hasPendingRequest ? (
                        <span className="text-sm text-primary-400">Requested</span>
                      ) : (
                        <button
                          onClick={() => onSendRequest(user.userId)}
                          className="btn-primary flex items-center gap-2"
                        >
                          <UserPlus className="w-4 h-4" />
                          Add Friend
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
